import type { ContentTreeNode, FsEntry } from '$lib/api/types.gen';
import { toTreePathFromFilePath } from '$lib/editor/path';
import { stripQueryEquals } from '$lib/utils/safe';

import {
	HISTORICAL_VERSION_QUERY_PARAM,
	HISTORY_COMPARE_QUERY_PARAM,
	HISTORY_GROUP_BASE_QUERY_PARAM,
	LEGACY_HISTORY_ITEM_QUERY_PARAM,
	normalizeHistoryComparisonSelector,
	normalizeHistoryGroupBaseSelector,
	sameEditUnit,
	type EditUnitIdentity,
	type HistoricalEditUnitScope,
} from './version';

type PageParams = { type?: string; id?: string; projectId?: string };
type EditorRouteParams = { type: string; id: string };
type StructuredValueHrefParams = EditorRouteParams & {
	fileId: string;
	propertyPath?: string;
	suffix?: string;
};

export const RAW_EDITOR_SUFFIX = 'raw';
export const FORM_EDITOR_SUFFIX = 'form';
export const GRAPH_EDITOR_SUFFIX = 'graph';
export const GRAPH_DEBUG_RUN_PARAM = 'debugRunId';
export const MAPS_EDITOR_SUFFIX = 'maps';
export const FOCUS_PARAM = 'focus';
export const FOCUS_NONCE_PARAM = 'focus-nonce';
export const ARTIFACTS_NODE_ID = '__agentic_artifacts__';

export const CHARACTERS_EDITOR_SUFFIX = 'characters';
export const CHARACTER_EDITOR_SUFFIX = 'character';
export const CHARACTER_APPEARANCE_EDITOR_SUFFIX = 'characterAppearance';
export const ITEMS_EDITOR_SUFFIX = 'items';
export const ITEM_EDITOR_SUFFIX = 'item';
export const ABILITIES_EDITOR_SUFFIX = 'abilities';
export const ABILITY_EDITOR_SUFFIX = 'ability';
export const SECRETS_EDITOR_SUFFIX = 'secrets';
export const SECRET_EDITOR_SUFFIX = 'secret';
export const STATUSES_EDITOR_SUFFIX = 'statuses';
export const STATUS_EDITOR_SUFFIX = 'status';
export const VARIABLES_EDITOR_SUFFIX = 'variables';
export const VARIABLE_EDITOR_SUFFIX = 'variable';
export const LOCATION_EDITOR_SUFFIX = 'location';
export const TAGS_EDITOR_SUFFIX = 'tags';
export const TAG_EDITOR_SUFFIX = 'tag';
export const MUSIC_TRACK_EDITOR_SUFFIX = 'musicTrack';
export const MUSIC_TRACKS_EDITOR_SUFFIX = 'musicTracks';
export const SOUND_EFFECT_EDITOR_SUFFIX = 'soundEffect';
export const SOUND_EFFECTS_EDITOR_SUFFIX = 'soundEffects';
export const VOICE_EDITOR_SUFFIX = 'voice';
export const VOICE_EDIT_EDITOR_SUFFIX = 'voiceEdit';
export const VOICES_EDITOR_SUFFIX = 'voices';
export const PLAYTEST_RUNS_EDITOR_SUFFIX = 'playtestRuns';
export const PLAYBOOKS_EDITOR_SUFFIX = 'playbooks';
export const PLAYBOOK_EDITOR_SUFFIX = 'playbook';
export const PLAYBOOK_TAGS_EDITOR_SUFFIX = 'playbookTags';
export const PLAYBOOK_TAG_EDITOR_SUFFIX = 'playbookTag';

export function playbookAnchorId(playbookId: string): string {
	return `playbook-${encodeURIComponent(playbookId)}`;
}

const ITEM_EDITOR_LIST_PATHS: Record<string, string> = {
	[CHARACTER_EDITOR_SUFFIX]: '/project/characters',
	[CHARACTER_APPEARANCE_EDITOR_SUFFIX]: '/project/characters',
	[LOCATION_EDITOR_SUFFIX]: '/project/locations',
	[ITEM_EDITOR_SUFFIX]: '/project/items',
	[ABILITY_EDITOR_SUFFIX]: '/project/abilities',
	[SECRET_EDITOR_SUFFIX]: '/project/secrets',
	[STATUS_EDITOR_SUFFIX]: '/project/statuses',
	[VARIABLE_EDITOR_SUFFIX]: '/project/variables',
	[TAG_EDITOR_SUFFIX]: '/project/tags',
	[VOICE_EDITOR_SUFFIX]: '/project/voices',
	[VOICE_EDIT_EDITOR_SUFFIX]: '/project/voices',
	[MUSIC_TRACK_EDITOR_SUFFIX]: '/project/music',
	[SOUND_EFFECT_EDITOR_SUFFIX]: '/project/soundEffects',
	[PLAYBOOK_EDITOR_SUFFIX]: '/project/runs/playbooks',
	[PLAYBOOK_TAG_EDITOR_SUFFIX]: '/project/runs/tags',
};

export const EDITOR_SUFFIXES = Object.freeze([
	FORM_EDITOR_SUFFIX,
	RAW_EDITOR_SUFFIX,
	GRAPH_EDITOR_SUFFIX,
	MAPS_EDITOR_SUFFIX,

	CHARACTERS_EDITOR_SUFFIX,
	CHARACTER_EDITOR_SUFFIX,
	CHARACTER_APPEARANCE_EDITOR_SUFFIX,
	ITEMS_EDITOR_SUFFIX,
	ITEM_EDITOR_SUFFIX,
	ABILITIES_EDITOR_SUFFIX,
	ABILITY_EDITOR_SUFFIX,
	SECRETS_EDITOR_SUFFIX,
	SECRET_EDITOR_SUFFIX,
	STATUSES_EDITOR_SUFFIX,
	STATUS_EDITOR_SUFFIX,
	VARIABLES_EDITOR_SUFFIX,
	VARIABLE_EDITOR_SUFFIX,
	LOCATION_EDITOR_SUFFIX,
	TAGS_EDITOR_SUFFIX,
	TAG_EDITOR_SUFFIX,
	MUSIC_TRACKS_EDITOR_SUFFIX,
	MUSIC_TRACK_EDITOR_SUFFIX,
	SOUND_EFFECTS_EDITOR_SUFFIX,
	SOUND_EFFECT_EDITOR_SUFFIX,
	VOICES_EDITOR_SUFFIX,
	VOICE_EDITOR_SUFFIX,
	VOICE_EDIT_EDITOR_SUFFIX,
	PLAYTEST_RUNS_EDITOR_SUFFIX,
	PLAYBOOKS_EDITOR_SUFFIX,
	PLAYBOOK_EDITOR_SUFFIX,
	PLAYBOOK_TAGS_EDITOR_SUFFIX,
	PLAYBOOK_TAG_EDITOR_SUFFIX,
] as const);

export type EditorSuffix = (typeof EDITOR_SUFFIXES)[number];

export type HistoricalEditorPolicy = Readonly<{
	kind: 'generic' | 'custom' | 'unsupported';
	aliases: readonly EditorSuffix[];
	requiresRootAsset: boolean;
}>;

const NO_HISTORICAL_EDITOR_ALIASES: readonly EditorSuffix[] = Object.freeze([]);
const UNSUPPORTED_HISTORICAL_EDITOR_POLICY: HistoricalEditorPolicy = Object.freeze({
	kind: 'unsupported',
	aliases: NO_HISTORICAL_EDITOR_ALIASES,
	requiresRootAsset: false,
});
const HISTORICAL_CUSTOM_EDITOR_POLICY: HistoricalEditorPolicy = Object.freeze({
	kind: 'custom',
	aliases: NO_HISTORICAL_EDITOR_ALIASES,
	requiresRootAsset: false,
});
const HISTORICAL_CUSTOM_EDITOR_WITH_ROOT_ASSET_POLICY: HistoricalEditorPolicy = Object.freeze({
	kind: 'custom',
	aliases: NO_HISTORICAL_EDITOR_ALIASES,
	requiresRootAsset: true,
});

const HISTORICAL_EDITOR_POLICIES = Object.freeze({
	[FORM_EDITOR_SUFFIX]: {
		kind: 'generic',
		aliases: NO_HISTORICAL_EDITOR_ALIASES,
		requiresRootAsset: false,
	},
	[RAW_EDITOR_SUFFIX]: {
		kind: 'generic',
		aliases: NO_HISTORICAL_EDITOR_ALIASES,
		requiresRootAsset: false,
	},
	[GRAPH_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_WITH_ROOT_ASSET_POLICY,
	[MAPS_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_WITH_ROOT_ASSET_POLICY,
	[CHARACTERS_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_WITH_ROOT_ASSET_POLICY,
	[CHARACTER_EDITOR_SUFFIX]: {
		kind: 'custom',
		aliases: Object.freeze([CHARACTER_APPEARANCE_EDITOR_SUFFIX]),
		requiresRootAsset: true,
	},
	[CHARACTER_APPEARANCE_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_WITH_ROOT_ASSET_POLICY,
	[ITEMS_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_WITH_ROOT_ASSET_POLICY,
	[ITEM_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_WITH_ROOT_ASSET_POLICY,
	[ABILITIES_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_POLICY,
	[ABILITY_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_WITH_ROOT_ASSET_POLICY,
	[SECRETS_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_POLICY,
	[SECRET_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_WITH_ROOT_ASSET_POLICY,
	[STATUSES_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_POLICY,
	[STATUS_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_WITH_ROOT_ASSET_POLICY,
	[VARIABLES_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_POLICY,
	[VARIABLE_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_POLICY,
	[LOCATION_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_WITH_ROOT_ASSET_POLICY,
	[TAGS_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_POLICY,
	[TAG_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_POLICY,
	[MUSIC_TRACKS_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_POLICY,
	[MUSIC_TRACK_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_POLICY,
	[SOUND_EFFECTS_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_POLICY,
	[SOUND_EFFECT_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_POLICY,
	[VOICES_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_POLICY,
	[VOICE_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_POLICY,
	[VOICE_EDIT_EDITOR_SUFFIX]: UNSUPPORTED_HISTORICAL_EDITOR_POLICY,
	[PLAYTEST_RUNS_EDITOR_SUFFIX]: UNSUPPORTED_HISTORICAL_EDITOR_POLICY,
	[PLAYBOOKS_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_POLICY,
	[PLAYBOOK_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_POLICY,
	[PLAYBOOK_TAGS_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_POLICY,
	[PLAYBOOK_TAG_EDITOR_SUFFIX]: HISTORICAL_CUSTOM_EDITOR_POLICY,
} as const satisfies Readonly<Record<EditorSuffix, HistoricalEditorPolicy>>);

export function isEditorSuffix(suffix: string): suffix is EditorSuffix {
	return (EDITOR_SUFFIXES as readonly string[]).includes(suffix);
}

export function historicalEditorPolicy(suffix: string): HistoricalEditorPolicy {
	return isEditorSuffix(suffix)
		? HISTORICAL_EDITOR_POLICIES[suffix]
		: UNSUPPORTED_HISTORICAL_EDITOR_POLICY;
}

export const RESERVED_PATH_SUFFIXES: readonly string[] = Object.freeze([
	...EDITOR_SUFFIXES,
	'changes',
]);

export type HistoricalRouteKind =
	| 'root'
	| 'overview'
	| 'tree'
	| 'file'
	| 'structured-type'
	| 'structured-value'
	| 'section'
	| 'project';

export type HistoricalDestination =
	| { kind: 'allowed'; route: HistoricalRouteKind; pathname: string }
	| { kind: 'normalize'; route: 'structured-value'; pathname: string }
	| { kind: 'current-only' };

export class LegacyHistoricalSelectorConflictError extends Error {
	constructor() {
		super('Legacy and canonical historical selectors cannot be combined');
		this.name = 'LegacyHistoricalSelectorConflictError';
	}
}

const CURRENT_ONLY_EDITOR_QUERY_PARAMS = new Set([
	LEGACY_HISTORY_ITEM_QUERY_PARAM,
	'rename',
	'delete',
	'create-folder',
	'create-structured-value',
	'structured-type-id',
	'upload-structured-type',
	'create-thread',
	'rename-thread',
	'invite',
	'create-link',
	'local-history',
	'saved-history',
	'search',
	'search-scope',
	'problems',
	'maximized',
	'runner',
	'runId',
	'debugRunId',
]);

export function editUnitIdentityFromURL(url: URL): EditUnitIdentity | undefined {
	const match = /^\/editor\/(edit-session|thread)\/([^/]+)(?:\/|$)/.exec(url.pathname);
	if (!match) return undefined;
	try {
		return {
			type: match[1] as EditUnitIdentity['type'],
			id: decodeURIComponent(match[2]),
		};
	} catch {
		return undefined;
	}
}

export function classifyHistoricalDestination(url: URL): HistoricalDestination {
	for (const parameter of CURRENT_ONLY_EDITOR_QUERY_PARAMS) {
		if (url.searchParams.has(parameter)) return { kind: 'current-only' };
	}

	const match = /^\/editor\/(?:edit-session|thread)\/[^/]+(\/.*)?$/.exec(url.pathname);
	if (!match) return { kind: 'current-only' };

	const suffix = (match[1] ?? '').replace(/\/+$/, '');
	if (!suffix) return { kind: 'allowed', route: 'root', pathname: url.pathname };
	if (suffix === '/overview') {
		return { kind: 'allowed', route: 'overview', pathname: url.pathname };
	}
	if (/^\/sections\/(project|world|mechanics|audio|testing)$/.test(suffix)) {
		return { kind: 'allowed', route: 'section', pathname: url.pathname };
	}
	if (/^\/(threads|marketing|builds|settings(?:\/(?:usage|invitations))?)$/.test(suffix)) {
		return { kind: 'allowed', route: 'project', pathname: url.pathname };
	}
	if (suffix === '/tree' || suffix.startsWith('/tree/')) {
		return { kind: 'allowed', route: 'tree', pathname: url.pathname };
	}
	if (/^\/file\/[^/]+$/.test(suffix)) {
		return { kind: 'allowed', route: 'file', pathname: url.pathname };
	}
	if (/^\/structured-type\/[^/]+$/.test(suffix)) {
		return { kind: 'allowed', route: 'structured-type', pathname: url.pathname };
	}

	const structuredValue = /^\/structured-value\/[^/]+(?:\/(.*))?$/.exec(suffix);
	if (!structuredValue) return { kind: 'current-only' };
	const propertyAndSuffix = structuredValue[1] ?? '';
	const segments = propertyAndSuffix.split('/').filter(Boolean);
	const routeSuffix = segments.at(-1);
	if (routeSuffix === 'changes') return { kind: 'current-only' };
	if (routeSuffix === RAW_EDITOR_SUFFIX && segments.length > 1) {
		return {
			kind: 'normalize',
			route: 'structured-value',
			pathname: `${url.pathname.slice(0, url.pathname.length - propertyAndSuffix.length)}${RAW_EDITOR_SUFFIX}`,
		};
	}
	if (routeSuffix && isEditorSuffix(routeSuffix)) {
		const policy = historicalEditorPolicy(routeSuffix);
		if (policy.kind === 'custom') {
			return { kind: 'allowed', route: 'structured-value', pathname: url.pathname };
		}
		if (policy.kind === 'generic') {
			return { kind: 'allowed', route: 'structured-value', pathname: url.pathname };
		}
		const normalized = [...segments.slice(0, -1), FORM_EDITOR_SUFFIX].join('/');
		return {
			kind: 'normalize',
			route: 'structured-value',
			pathname: `${url.pathname.slice(0, url.pathname.length - propertyAndSuffix.length)}${normalized}`,
		};
	}

	return { kind: 'allowed', route: 'structured-value', pathname: url.pathname };
}

function copyHistoricalFocus(source: URL, destination: URL): void {
	const classification = classifyHistoricalDestination(destination);
	if (classification.kind === 'current-only' || classification.route !== 'structured-value') return;
	for (const key of [FOCUS_PARAM, FOCUS_NONCE_PARAM]) {
		const value = source.searchParams.get(key);
		if (value !== null) destination.searchParams.set(key, value);
	}
}

export function normalizedHistoricalCustomEditorURL(destination: URL): URL | undefined {
	const classification = classifyHistoricalDestination(destination);
	if (classification.kind !== 'normalize' || classification.pathname.endsWith('/raw')) {
		return undefined;
	}
	const selector = destination.searchParams.get(HISTORICAL_VERSION_QUERY_PARAM);
	if (selector === null) return undefined;
	const compareTo = normalizeHistoryComparisonSelector(
		destination.searchParams.get(HISTORY_COMPARE_QUERY_PARAM),
	);
	const groupBase = normalizeHistoryGroupBaseSelector(
		destination.searchParams.get(HISTORY_GROUP_BASE_QUERY_PARAM),
	);

	const normalized = new URL(destination);
	normalized.pathname = classification.pathname;
	normalized.search = '';
	normalized.searchParams.set(HISTORICAL_VERSION_QUERY_PARAM, selector);
	if (compareTo !== null) normalized.searchParams.set(HISTORY_COMPARE_QUERY_PARAM, compareTo);
	else if (groupBase !== null)
		normalized.searchParams.set(HISTORY_GROUP_BASE_QUERY_PARAM, groupBase);
	copyHistoricalFocus(destination, normalized);
	return normalized;
}

export function canonicalLegacyHistoricalURL(destination: URL): URL | undefined {
	if (!destination.searchParams.has(LEGACY_HISTORY_ITEM_QUERY_PARAM)) return undefined;
	if (destination.searchParams.has(HISTORICAL_VERSION_QUERY_PARAM)) {
		throw new LegacyHistoricalSelectorConflictError();
	}

	const selector = destination.searchParams.get(LEGACY_HISTORY_ITEM_QUERY_PARAM) ?? '';
	const compareTo = normalizeHistoryComparisonSelector(
		destination.searchParams.get(HISTORY_COMPARE_QUERY_PARAM),
	);
	const groupBase = normalizeHistoryGroupBaseSelector(
		destination.searchParams.get(HISTORY_GROUP_BASE_QUERY_PARAM),
	);
	const preserveStructuredFallback = destination.searchParams.get('editor-error') === 'structured';
	const canonical = new URL(destination);
	canonical.search = '';
	canonical.searchParams.set(HISTORICAL_VERSION_QUERY_PARAM, selector);
	if (compareTo !== null) canonical.searchParams.set(HISTORY_COMPARE_QUERY_PARAM, compareTo);
	else if (groupBase !== null)
		canonical.searchParams.set(HISTORY_GROUP_BASE_QUERY_PARAM, groupBase);
	if (preserveStructuredFallback) canonical.searchParams.set('editor-error', 'structured');
	copyHistoricalFocus(destination, canonical);
	return canonical;
}

export function withHistoricalVersion(
	destination: URL,
	scope: HistoricalEditUnitScope,
): URL | undefined {
	const destinationEditUnit = editUnitIdentityFromURL(destination);
	if (!destinationEditUnit || !sameEditUnit(destinationEditUnit, scope)) return undefined;

	const classification = classifyHistoricalDestination(destination);
	if (classification.kind === 'current-only') return undefined;

	const historicalURL = new URL(destination);
	const compareTo = normalizeHistoryComparisonSelector(
		destination.searchParams.get(HISTORY_COMPARE_QUERY_PARAM),
	);
	const groupBase = normalizeHistoryGroupBaseSelector(
		destination.searchParams.get(HISTORY_GROUP_BASE_QUERY_PARAM),
	);
	historicalURL.pathname = classification.pathname;
	const structuredFallback =
		classification.route === 'structured-value' &&
		historicalURL.pathname.endsWith(`/${RAW_EDITOR_SUFFIX}`) &&
		destination.searchParams.get('editor-error') === 'structured';
	historicalURL.search = '';
	historicalURL.searchParams.set(HISTORICAL_VERSION_QUERY_PARAM, scope.versionId);
	if (classification.route === 'project') {
		for (const key of ['view', 'marketing_id', 'userId', 'playbookId']) {
			const value = destination.searchParams.get(key);
			if (value !== null) historicalURL.searchParams.set(key, value);
		}
	}
	if (compareTo !== null) historicalURL.searchParams.set(HISTORY_COMPARE_QUERY_PARAM, compareTo);
	else if (groupBase !== null)
		historicalURL.searchParams.set(HISTORY_GROUP_BASE_QUERY_PARAM, groupBase);
	if (structuredFallback) historicalURL.searchParams.set('editor-error', 'structured');
	copyHistoricalFocus(destination, historicalURL);
	return historicalURL;
}

export function withoutHistoricalVersion(destination: URL): URL {
	const currentURL = new URL(destination);
	currentURL.searchParams.delete(HISTORICAL_VERSION_QUERY_PARAM);
	currentURL.searchParams.delete(LEGACY_HISTORY_ITEM_QUERY_PARAM);
	currentURL.searchParams.delete(HISTORY_COMPARE_QUERY_PARAM);
	currentURL.searchParams.delete(HISTORY_GROUP_BASE_QUERY_PARAM);
	if (currentURL.searchParams.get('editor-error') === 'structured') {
		currentURL.searchParams.delete('editor-error');
	}
	return currentURL;
}

/** Carry a folded selection across content links that only encode the snapshot. */
export function withHistoricalGroupNavigation(current: URL, destination: URL): URL | undefined {
	const baseline = normalizeHistoryGroupBaseSelector(
		current.searchParams.get(HISTORY_GROUP_BASE_QUERY_PARAM),
	);
	const identity = editUnitIdentityFromURL(current);
	const targetIdentity = editUnitIdentityFromURL(destination);
	if (
		!baseline ||
		!identity ||
		!targetIdentity ||
		!sameEditUnit(identity, targetIdentity) ||
		current.origin !== destination.origin ||
		current.searchParams.get(HISTORICAL_VERSION_QUERY_PARAM) !==
			destination.searchParams.get(HISTORICAL_VERSION_QUERY_PARAM) ||
		!destination.searchParams.has(HISTORICAL_VERSION_QUERY_PARAM) ||
		destination.searchParams.has(HISTORY_COMPARE_QUERY_PARAM) ||
		destination.searchParams.has(HISTORY_GROUP_BASE_QUERY_PARAM) ||
		(current.pathname === destination.pathname && current.hash === destination.hash) ||
		classifyHistoricalDestination(destination).kind === 'current-only'
	)
		return undefined;
	const result = new URL(destination);
	result.searchParams.set(HISTORY_GROUP_BASE_QUERY_PARAM, baseline);
	return result;
}

export function isVoiceItemPropertyPath(path: string | undefined): boolean {
	return /^\/project\/voices\/[^/]+$/.test(path ?? '');
}

export function isVoiceEditorPath(pathname: string): boolean {
	return pathname.endsWith(`/${VOICE_EDITOR_SUFFIX}`);
}

export function isItemEditorPath(pathname: string): boolean {
	const suffix = pathname.split('/').at(-1) ?? '';
	return ITEM_EDITOR_LIST_PATHS[suffix] !== undefined;
}

export function isItemEditorPropertyPath(pathname: string, propertyPath: string): boolean {
	const suffix = pathname.split('/').at(-1) ?? '';
	const listPath = ITEM_EDITOR_LIST_PATHS[suffix];
	if (!listPath) return false;
	const itemPrefix = `${listPath}/`;
	if (!propertyPath.startsWith(itemPrefix)) return false;
	const itemKey = propertyPath.slice(itemPrefix.length);
	return /^[^/]+$/.test(itemKey);
}

export function escapeJsonPointerSegment(segment: string | number): string {
	return String(segment).replaceAll('~', '~0').replaceAll('/', '~1');
}

export function editorSuffixFromXEditor(xEditor: unknown): string {
	switch (xEditor) {
		case 'visual_node_graph':
			return GRAPH_EDITOR_SUFFIX;
		case 'maps':
		case 'locationlist':
			return MAPS_EDITOR_SUFFIX;
		case 'characterlist':
			return CHARACTERS_EDITOR_SUFFIX;
		case 'character':
			return CHARACTER_EDITOR_SUFFIX;
		case 'itemlist':
			return ITEMS_EDITOR_SUFFIX;
		case 'item':
			return ITEM_EDITOR_SUFFIX;
		case 'abilitylist':
			return ABILITIES_EDITOR_SUFFIX;
		case 'ability':
			return ABILITY_EDITOR_SUFFIX;
		case 'secretlist':
			return SECRETS_EDITOR_SUFFIX;
		case 'secret':
			return SECRET_EDITOR_SUFFIX;
		case 'statuslist':
			return STATUSES_EDITOR_SUFFIX;
		case 'status':
			return STATUS_EDITOR_SUFFIX;
		case 'variablelist':
			return VARIABLES_EDITOR_SUFFIX;
		case 'variable':
			return VARIABLE_EDITOR_SUFFIX;
		case 'location':
			return LOCATION_EDITOR_SUFFIX;
		case 'taglist':
			return TAGS_EDITOR_SUFFIX;
		case 'tag':
			return TAG_EDITOR_SUFFIX;
		case 'musiclist':
			return MUSIC_TRACKS_EDITOR_SUFFIX;
		case 'music':
			return MUSIC_TRACK_EDITOR_SUFFIX;
		case 'soundeffectlist':
			return SOUND_EFFECTS_EDITOR_SUFFIX;
		case 'soundeffect':
			return SOUND_EFFECT_EDITOR_SUFFIX;
		case 'voicelist':
			return VOICES_EDITOR_SUFFIX;
		case 'voice':
			return VOICE_EDITOR_SUFFIX;
		case 'playtestruns':
			return PLAYTEST_RUNS_EDITOR_SUFFIX;
		case 'playbooklist':
			return PLAYBOOKS_EDITOR_SUFFIX;
		case 'playbook':
			return PLAYBOOK_EDITOR_SUFFIX;
		case 'playbooktaglist':
			return PLAYBOOK_TAGS_EDITOR_SUFFIX;
		case 'playbooktag':
			return PLAYBOOK_TAG_EDITOR_SUFFIX;
		default:
			return FORM_EDITOR_SUFFIX;
	}
}

export function escapeStructuredPropertyPath(path: string | string[]): string {
	const components = typeof path === 'string' ? path.split('/') : path;
	if (components.length === 0) return '';
	const lastIndex = components.length - 1;
	const lastComponent = components[lastIndex];
	if (!RESERVED_PATH_SUFFIXES.includes(lastComponent)) return components.join('/');
	const escaped = [...components.slice(0, lastIndex), `~${lastComponent}`];
	return escaped.join('/');
}

/**
 * Builds the editor href for a single item within a structured-value list page
 * (e.g. one variable/character/item/secret/tag).
 *
 * `subvaluePath` must be the *unescaped* JSON Pointer of the parent list, as
 * exposed by the structured-value layout's `data.subvaluePath`. The item key is
 * appended and the resulting property path is re-escaped for the route, so the
 * reserved-suffix escaping stays on the (now non-trailing) list segment instead
 * of leaking through as a stale mid-path `~` escape. `suffix` is the per-item
 * editor route segment (`variable`, `character`, …).
 */
export function structuredValueItemHref(
	page: { params: Record<string, string | undefined>; url: { search: string } },
	subvaluePath: string,
	itemKey: string | number,
	suffix: string,
): string {
	const normalizedSubvaluePath = '/' + subvaluePath.replace(/^\/+|\/+$/g, '');
	const expectedListPath = ITEM_EDITOR_LIST_PATHS[suffix];
	if (expectedListPath && normalizedSubvaluePath !== expectedListPath) {
		throw new Error(
			`structuredValueItemHref expected list path "${expectedListPath}", received "${subvaluePath}"`,
		);
	}
	const { type, id, svid } = page.params;
	const childPath = escapeStructuredPropertyPath(
		`${subvaluePath}/${escapeJsonPointerSegment(itemKey)}`.replace(/^\/+/, ''),
	);
	return `/editor/${type}/${id}/structured-value/${svid}/${childPath}/${suffix}${page.url.search}`;
}

export function studioItemHref(
	page: { params: Record<string, string | undefined>; url: { search: string } },
	itemKey: string | number,
	suffix: string,
): string {
	const listPath = ITEM_EDITOR_LIST_PATHS[suffix];
	if (!listPath) throw new Error(`Unknown studio item editor suffix "${suffix}"`);
	const { type, id, svid } = page.params;
	const propertyPath = escapeStructuredPropertyPath(
		`${listPath}/${escapeJsonPointerSegment(itemKey)}`.replace(/^\/+/, ''),
	);
	return `/editor/${type}/${id}/structured-value/${svid}/${propertyPath}/${suffix}${page.url.search}`;
}

export function splitStructuredPathRouteSuffix(path: string | undefined): {
	propertyPath: string;
	routeSuffix?: string;
} {
	const normalizedPath = path?.replace(/^\/+/, '') ?? '';
	if (!normalizedPath) return { propertyPath: '' };

	const segments = normalizedPath.split('/').filter(Boolean);
	const trailingSegment = segments.at(-1);
	if (!trailingSegment || !RESERVED_PATH_SUFFIXES.includes(trailingSegment)) {
		return { propertyPath: normalizedPath };
	}

	return {
		propertyPath: segments.slice(0, -1).join('/'),
		routeSuffix: trailingSegment,
	};
}

export function unescapeStructuredPropertyPath(path: string | string[]): string {
	const components = typeof path === 'string' ? path.split('/') : path;
	if (components.length === 0) return '';
	const lastIndex = components.length - 1;
	const lastComponent = components[lastIndex];
	if (!lastComponent.startsWith('~') || !RESERVED_PATH_SUFFIXES.includes(lastComponent.slice(1))) {
		return components.join('/');
	}
	const unescaped = [...components.slice(0, lastIndex), lastComponent.slice(1)];
	return unescaped.join('/');
}

export function appendQueryParam(href: string, key: string, value?: string): string {
	const [pathname, search = ''] = href.split('?');
	const searchParams = new URLSearchParams(search);
	if (value === undefined) {
		searchParams.set(key, '');
	} else {
		searchParams.set(key, value);
	}
	const resolvedSearch = searchParams.toString().replace(/=$/, '');
	return resolvedSearch ? `${pathname}?${resolvedSearch}` : pathname;
}

export function editorContextHref({ type, id }: EditorRouteParams): string {
	return `/editor/${type}/${id}`;
}

export function structuredValueHref({
	type,
	id,
	fileId,
	propertyPath = '',
	suffix,
}: StructuredValueHrefParams): string {
	const normalizedPropertyPath = propertyPath.replace(/^\/+/, '');
	const escapedPath = normalizedPropertyPath
		? `/${escapeStructuredPropertyPath(normalizedPropertyPath)}`
		: '';
	const suffixPath = suffix ? `/${suffix}` : '';
	return `${editorContextHref({ type, id })}/structured-value/${fileId}${escapedPath}${suffixPath}`;
}

export function graphDebuggerHref({
	runId,
	...hrefParams
}: EditorRouteParams & { fileId: string; runId: string }): string {
	return appendQueryParam(
		structuredValueHref({
			...hrefParams,
			propertyPath: 'project/graphs',
			suffix: GRAPH_EDITOR_SUFFIX,
		}),
		GRAPH_DEBUG_RUN_PARAM,
		runId,
	);
}

export function playtestRunHref({
	runId,
	...hrefParams
}: EditorRouteParams & { fileId: string; runId: string }): string {
	return appendQueryParam(
		structuredValueHref({
			...hrefParams,
			propertyPath: 'project/runs',
			suffix: PLAYTEST_RUNS_EDITOR_SUFFIX,
		}),
		'runId',
		runId,
	);
}

function setFocusTargetSearchParams(
	searchParams: URLSearchParams,
	{
		focus,
		nonce,
		appendExistingFocus = false,
	}: {
		focus?: string;
		nonce?: string;
		appendExistingFocus?: boolean;
	},
): URLSearchParams {
	const existingFocus = appendExistingFocus ? searchParams.get(FOCUS_PARAM) : undefined;
	searchParams.delete(FOCUS_PARAM);
	searchParams.delete(FOCUS_NONCE_PARAM);
	if (focus !== undefined) {
		searchParams.set(FOCUS_PARAM, existingFocus ? `${focus}${existingFocus}` : focus);
	}
	if (nonce !== undefined) {
		searchParams.set(FOCUS_NONCE_PARAM, nonce);
	}
	return searchParams;
}

export function structuredValueHrefWithFocus({
	focus,
	nonce,
	existingUrlSearch,
	...hrefParams
}: StructuredValueHrefParams & {
	focus?: string;
	nonce?: string;
	existingUrlSearch?: string | URLSearchParams;
}): string {
	const href = structuredValueHref(hrefParams);
	const searchParams = existingUrlSearch
		? new URLSearchParams(existingUrlSearch)
		: new URLSearchParams();

	if (focus !== undefined) {
		setFocusTargetSearchParams(searchParams, { focus, nonce, appendExistingFocus: true });
	}

	const resolvedSearch = stripQueryEquals(searchParams.toString());
	return resolvedSearch ? `${href}?${resolvedSearch}` : href;
}

export function stampFocusNonce(
	event: Event,
	{ allowPendingFocus = false }: { allowPendingFocus?: boolean } = {},
): void {
	if (event.type === 'keydown' && (event as KeyboardEvent).key !== 'Enter') return;
	if ('button' in event && event.button !== 0) return;
	const anchor = event.currentTarget as HTMLAnchorElement | null;
	if (!anchor?.getAttribute('href')) return;
	const url = new URL(anchor.href);
	// Object references acquire their focus path in the editor redirect.
	if (!url.searchParams.has(FOCUS_PARAM) && !allowPendingFocus) return;
	url.searchParams.set(FOCUS_NONCE_PARAM, crypto.randomUUID());
	anchor.href = url.toString();
}

export function withFocusTargetQuery(href: URL | string, focus?: string, nonce?: string): URL {
	const url = href instanceof URL ? new URL(href) : new URL(href, window.location.href);
	setFocusTargetSearchParams(url.searchParams, { focus, nonce });
	return url;
}

export function clearFocusTargetQuery(href: URL | string): URL {
	const url = href instanceof URL ? new URL(href) : new URL(href, window.location.href);
	setFocusTargetSearchParams(url.searchParams, {});
	url.search = stripQueryEquals(url.search);
	return url;
}

export function clearFocusNonceQuery(href: URL | string): URL {
	const url = href instanceof URL ? new URL(href) : new URL(href, window.location.href);
	url.searchParams.delete(FOCUS_NONCE_PARAM);
	url.search = stripQueryEquals(url.search);
	return url;
}

export function structuredValueOrPropertyPath(
	node: ContentTreeNode,
	pageParams: PageParams,
): string | undefined {
	if (!node.structured_property) return undefined;
	const basePath = pageParams.projectId
		? `/editor/project/${pageParams.projectId}`
		: pageParams.type && pageParams.id
			? `/editor/${pageParams.type}/${pageParams.id}`
			: undefined;
	if (!basePath) return undefined;
	if (pageParams.projectId) {
		return `${basePath}/structured-value/${node.structured_property.file_id}`;
	}
	const path = /^\/?$/.test(node.structured_property.property_path)
		? ''
		: '/' + escapeStructuredPropertyPath(node.structured_property.property_path.replace(/^\//, ''));
	const suffix = editorSuffixFromXEditor(node.structured_property.editor_hint);
	const editorSuffix = suffix !== FORM_EDITOR_SUFFIX ? `/${suffix}` : '';

	return `${basePath}/structured-value/${node.structured_property.file_id}${path}${editorSuffix}`;
}

export interface NodeNavigationParams {
	projectId?: string;
	type?: 'edit-session' | 'thread';
	id?: string;
}

export function nodeToHref(
	node: ContentTreeNode | undefined,
	params: NodeNavigationParams,
): string | null {
	if (!node) return null;

	const basePath = params.projectId
		? `/editor/project/${params.projectId}`
		: params.type && params.id
			? `/editor/${params.type}/${params.id}`
			: null;

	if (!basePath) return null;

	if (node.id === ARTIFACTS_NODE_ID) return params.projectId ? null : `${basePath}/artifacts`;

	if (node.node_type === 'folder') {
		const folderPath = node.fs?.path ?? '';
		return `${basePath}/tree${folderPath}`;
	}
	if (node.structured_property) {
		return (
			structuredValueOrPropertyPath(node, {
				projectId: params.projectId,
				type: params.type,
				id: params.id,
			}) ?? null
		);
	}

	if (node.structured) {
		if (node.fs?.mime_type?.startsWith('application/vnd.dreamstudio.type')) {
			return `${basePath}/structured-type/${node.id}`;
		}
		return `${basePath}/structured-value/${node.id}`;
	}

	return `${basePath}/file/${node.id}`;
}

export function fsEntryToHref(
	fsEntry: Pick<FsEntry, 'id' | 'file_path' | 'is_folder' | 'mime_type'>,
	params: NodeNavigationParams,
): string | null {
	const basePath = params.projectId
		? `/editor/project/${params.projectId}`
		: params.type && params.id
			? `/editor/${params.type}/${params.id}`
			: null;

	if (!basePath) return null;

	if (fsEntry.is_folder) {
		return `${basePath}${toTreePathFromFilePath(fsEntry.file_path)}`;
	}

	if (fsEntry.mime_type?.startsWith('application/vnd.dreamstudio.type')) {
		return `${basePath}/structured-type/${fsEntry.id}`;
	}

	if (fsEntry.mime_type?.startsWith('application/vnd.dreamstudio.value')) {
		return `${basePath}/structured-value/${fsEntry.id}`;
	}

	return `${basePath}/file/${fsEntry.id}`;
}
